/**
 * Depoimentos Carousel
 * Handles testimonials slider functionality
 */

interface CarouselState {
  currentIndex: number;
  totalSlides: number;
  slidesPerView: number;
  maxIndex: number;
}

class DepoimentosCarousel {
  private carousel: HTMLElement;
  private track: HTMLElement;
  private prevBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private dotsContainer: HTMLElement;
  private state: CarouselState;

  constructor(carousel: HTMLElement) {
    this.carousel = carousel;
    this.track = carousel.querySelector('.depoimentos-track') as HTMLElement;
    this.prevBtn = carousel.parentElement?.querySelector('.depoimentos-prev') as HTMLButtonElement;
    this.nextBtn = carousel.parentElement?.querySelector('.depoimentos-next') as HTMLButtonElement;
    this.dotsContainer = carousel.parentElement?.querySelector('.depoimentos-dots') as HTMLElement;

    if (!this.track || !this.prevBtn || !this.nextBtn || !this.dotsContainer) {
      return;
    }

    const slides = this.track.querySelectorAll('.depoimentos-slide');
    
    this.state = {
      currentIndex: 0,
      totalSlides: slides.length,
      slidesPerView: this.getSlidesPerView(),
      maxIndex: 0
    };

    this.calculateMaxIndex();
    this.init();
  }

  private getSlidesPerView(): number {
    const width = window.innerWidth;
    if (width >= 1024) return 3; // lg
    if (width >= 768) return 2;  // md
    return 1; // mobile
  }

  private calculateMaxIndex(): void {
    this.state.maxIndex = Math.max(0, this.state.totalSlides - this.state.slidesPerView);
  }

  private init(): void {
    // Create dots
    this.createDots();

    // Event listeners
    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());

    // Handle resize
    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const oldSlidesPerView = this.state.slidesPerView;
        this.state.slidesPerView = this.getSlidesPerView();
        
        if (oldSlidesPerView !== this.state.slidesPerView) {
          this.calculateMaxIndex();
          this.state.currentIndex = Math.min(this.state.currentIndex, this.state.maxIndex);
          this.createDots();
          this.updateCarousel();
        }
      }, 250);
    });

    // Auto-play (opcional)
    this.startAutoPlay();

    // Initial update
    this.updateCarousel();
  }

  private createDots(): void {
    this.dotsContainer.innerHTML = '';
    
    const totalDots = this.state.maxIndex + 1;
    
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'w-2 h-2 rounded-full transition-all';
      dot.setAttribute('aria-label', `Ir para slide ${i + 1}`);
      
      dot.addEventListener('click', () => {
        this.state.currentIndex = i;
        this.updateCarousel();
      });

      this.dotsContainer.appendChild(dot);
    }
  }

  private updateCarousel(): void {
    // Move track
    const percentage = -(this.state.currentIndex * (100 / this.state.slidesPerView));
    this.track.style.transform = `translateX(${percentage}%)`;

    // Update buttons
    this.prevBtn.disabled = this.state.currentIndex === 0;
    this.nextBtn.disabled = this.state.currentIndex >= this.state.maxIndex;

    this.prevBtn.style.opacity = this.state.currentIndex === 0 ? '0.5' : '1';
    this.nextBtn.style.opacity = this.state.currentIndex >= this.state.maxIndex ? '0.5' : '1';

    // Update dots
    const dots = this.dotsContainer.querySelectorAll('button');
    dots.forEach((dot, index) => {
      if (index === this.state.currentIndex) {
        dot.classList.add('bg-custom-2', 'w-8');
        dot.classList.remove('bg-gray-3');
      } else {
        dot.classList.add('bg-gray-3');
        dot.classList.remove('bg-custom-2', 'w-8');
      }
    });
  }

  private prev(): void {
    if (this.state.currentIndex > 0) {
      this.state.currentIndex--;
      this.updateCarousel();
      this.resetAutoPlay();
    }
  }

  private next(): void {
    if (this.state.currentIndex < this.state.maxIndex) {
      this.state.currentIndex++;
      this.updateCarousel();
      this.resetAutoPlay();
    }
  }

  private autoPlayInterval?: ReturnType<typeof setInterval>;

  private startAutoPlay(): void {
    this.autoPlayInterval = setInterval(() => {
      if (this.state.currentIndex >= this.state.maxIndex) {
        this.state.currentIndex = 0;
      } else {
        this.state.currentIndex++;
      }
      this.updateCarousel();
    }, 5000); // Change slide every 5 seconds
  }

  private resetAutoPlay(): void {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.startAutoPlay();
    }
  }
}

// Initialize all carousels
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.depoimentos-carousel');
  carousels.forEach((carousel) => {
    new DepoimentosCarousel(carousel as HTMLElement);
  });
});


